import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { ArrowRight } from "lucide-react";

export const Route = createFileRoute("/login")({
  head: () => ({ meta: [{ title: "Logga in – AK Husbil & Fritid" }, { name: "robots", content: "noindex, nofollow" }] }),
  validateSearch: (s: Record<string, unknown>): { next?: string } =>
    typeof s.next === "string" ? { next: s.next } : {},

  component: LoginPage,
});

function safeNext(next: string | undefined): string {
  if (!next) return "/";
  // Only allow same-origin relative paths
  if (!next.startsWith("/") || next.startsWith("//")) return "/";
  return next;
}

function LoginPage() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const target = safeNext(next);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) window.location.href = target;
    });
  }, [target]);

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email, password,
          options: { emailRedirectTo: `${window.location.origin}${target}` },
        });
        if (error) throw error;
        toast.success("Konto skapat. Kolla din e-post om bekräftelse krävs.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Inloggad");
        window.location.href = target;
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Något gick fel");
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}${target}`,
      });
      if (result.error) {
        toast.error(result.error.message ?? "Google-inloggning misslyckades");
        return;
      }
      if (result.redirected) return;
      window.location.href = target;
    } finally {
      setLoading(false);
    }
  }


  return (
    <section className="bg-warm">
      <div className="container-edge flex min-h-[80vh] max-w-md flex-col justify-center py-20">
        <div className="rounded-3xl border border-border bg-card p-8 md:p-10 elev-shadow">
          <p className="eyebrow">{mode === "signin" ? "Logga in" : "Skapa konto"}</p>
          <h1 className="mt-3 font-display text-4xl leading-[1.05] md:text-5xl">
            {mode === "signin" ? (
              <>Välkommen <span className="font-italic-display text-accent">tillbaka</span>.</>
            ) : (
              <>Skapa <span className="font-italic-display text-accent">konto</span>.</>
            )}
          </h1>

          <button
            onClick={handleGoogle}
            disabled={loading}
            className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full border border-border bg-background px-5 py-3.5 text-sm font-medium transition hover:bg-secondary disabled:opacity-60"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2C29.3 35.2 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.6 5.1C9.6 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.2-4.5 5.6l6.2 5.2C40.6 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
            Fortsätt med Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> eller <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={handleEmail} className="space-y-3">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-post"
              className="w-full rounded-full border border-input bg-background px-5 py-3 text-base outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
            <input
              type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Lösenord"
              className="w-full rounded-full border border-input bg-background px-5 py-3 text-base outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
            <button
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
            >
              {mode === "signin" ? "Logga in" : "Skapa konto"} <ArrowRight size={16} />
            </button>
          </form>

          <button
            onClick={() => setMode((m) => (m === "signin" ? "signup" : "signin"))}
            className="mt-6 text-sm text-muted-foreground hover:text-accent"
          >
            {mode === "signin" ? "Inget konto? Skapa ett →" : "Har du redan ett konto? Logga in →"}
          </button>
        </div>
      </div>
    </section>
  );
}
